import { ORG_UNITS, USERS } from './data';
import type { AccessEntry, AccessRole, Principal, SharingPermissions, SharingState, Viewer, ViewerId } from './types';

/** Prototype personas. Their rights come only from the current shares, never from the persona itself. */
export const VIEWERS: Record<ViewerId, Viewer> = {
    owner: { id: 'owner', label: 'Propriétaire', userId: 'u-jm', description: 'Julie Moreau a créé le réseau.' },
    write: { id: 'write', label: 'Écriture', userId: 'u-tr', description: 'Thomas Roux est membre de l’équipe AO Métropole Lilloise.' },
    read: { id: 'read', label: 'Lecture', userId: 'u-lp', description: 'Louise Petit est dans l’Équipe Commercial IDF, filière Réseaux de chaleur IDF.' },
};

export const VIEWER_IDS = Object.keys(VIEWERS) as ViewerId[];

export function isViewerId(value: string | null): value is ViewerId {
    return value !== null && (VIEWER_IDS as string[]).includes(value);
}

export function directory(state: SharingState): Principal[] {
    return [...USERS, ...state.aoTeams, ...ORG_UNITS];
}

export function findPrincipal(state: SharingState, id: string): Principal | undefined {
    return directory(state).find((p) => p.id === id);
}

export function memberCount(principal: Principal): number {
    if (principal.kind === 'user') return 1;
    return principal.memberIds?.length ?? principal.members ?? 0;
}

/** Ids of every group the user belongs to: the org chain upwards and any AO team. */
export function groupsOf(state: SharingState, userId: string): string[] {
    const ids: string[] = [];
    let parentId = USERS.find((u) => u.id === userId)?.parentId;
    while (parentId) {
        ids.push(parentId);
        parentId = ORG_UNITS.find((o) => o.id === parentId)?.parentId;
    }
    state.aoTeams.filter((t) => t.memberIds?.includes(userId)).forEach((t) => ids.push(t.id));
    return ids;
}

/** The highest right among direct and group shares applies (hypothesis under review). */
export function computePermissions(state: SharingState, viewer: Viewer): SharingPermissions {
    if (viewer.userId === state.ownerId) return { role: 'owner', canRead: true, canManage: true, canTransfer: true, via: [] };
    const reach = new Set([viewer.userId, ...groupsOf(state, viewer.userId)]);
    const applicable = state.grants.filter((g) => reach.has(g.principalId));
    const role: AccessRole | null = applicable.some((g) => g.role === 'write') ? 'write' : applicable.length ? 'read' : null;
    const via = applicable
        .filter((g) => g.role === role)
        .map((g) => (g.principalId === viewer.userId ? 'partage direct' : findPrincipal(state, g.principalId)?.name ?? ''));
    return { role, canRead: role !== null, canManage: role === 'write', canTransfer: false, via };
}

export function accessEntries(state: SharingState): AccessEntry[] {
    const owner = findPrincipal(state, state.ownerId);
    const entries: AccessEntry[] = owner ? [{ principal: owner, role: 'owner' }] : [];
    state.grants.forEach((g) => {
        const principal = findPrincipal(state, g.principalId);
        if (principal) entries.push({ principal, role: g.role });
    });
    return entries;
}
